<template>
	<div class="sentiment-combined-container">
		<div class="d-flex flex-wrap justify-content-between align-items-center mb-2 px-2">
			<div>
				<h5 class="chart-main-title mb-0">{{ title }}</h5>
				<small class="chart-sub-title">{{ subText }}</small>
			</div>
			<div class="d-flex flex-wrap align-items-center toolbar-group mt-2 mt-md-0">
				<!-- 显示模式切换 -->
				<b-button-group size="sm" class="mr-2 mb-1">
					<b-button
						:variant="displayMode === 'combo' ? 'primary' : 'outline-secondary'"
						@click="setDisplayMode('combo')"
						title="结构1柱状图 + 结构2折线图，同屏对比最清晰"
					>
						柱线对比
					</b-button>
					<b-button
						:variant="displayMode === 'bar' ? 'primary' : 'outline-secondary'"
						@click="setDisplayMode('bar')"
						title="结构1与结构2双柱并列"
					>
						双柱对比
					</b-button>
					<b-button
						:variant="displayMode === 'line' ? 'primary' : 'outline-secondary'"
						@click="setDisplayMode('line')"
						title="结构1与结构2双折线"
					>
						双折线
					</b-button>
				</b-button-group>

				<!-- 坐标轴模式 -->
				<b-button-group size="sm" class="mr-2 mb-1">
					<b-button
						:variant="axisMode === 'dual' ? 'info' : 'outline-secondary'"
						@click="setAxisMode('dual')"
						title="左轴0-400，右轴0-8000，按1:20等比对其水平网格"
					>
						双轴等比
					</b-button>
					<b-button
						:variant="axisMode === 'single' ? 'info' : 'outline-secondary'"
						@click="setAxisMode('single')"
						title="以100为牛市基准分，统一单Y轴"
					>
						单轴归一
					</b-button>
				</b-button-group>

				<!-- 量能副图开关 -->
				<b-button
					size="sm"
					class="mb-1"
					:variant="showSubGrid ? 'success' : 'outline-secondary'"
					@click="toggleSubGrid"
				>
					{{ showSubGrid ? '隐藏副图' : '量能副图' }}
				</b-button>
			</div>
		</div>

		<!-- ECharts 图表 -->
		<v-chart
			ref="combinedChart"
			class="chart_combined"
			:style="{ height: chartHeight + 'px' }"
			:options="line"
			autoresize
			@datazoom="onDataZoom($event, line)"
		/>

		<!-- 说明卡片 -->
		<div class="mt-2 small explanation-box p-3">
			<div class="row">
				<div class="col-12 col-md-7 mb-2 mb-md-0">
					<div class="font-weight-bold mb-1 box-heading">
						📊 市场结构大周期对照说明：
					</div>
					<ul class="mb-0 pl-3">
						<li>
							<span class="badge badge-danger">牛市共振 (100+ / 2000+)</span>：
							结构1得分 &gt; 100 且 结构2成交额 &gt; 2000 亿，趋势大盘子蓝筹显著抱团，为确定性大牛市周期（背景粉色标记）。
						</li>
						<li>
							<span class="badge badge-primary">震荡行情 (50-100 / 1000-2000)</span>：
							题材热点活跃，指数偏震荡，结构分化，以局部轮动为主。
						</li>
						<li>
							<span class="badge badge-warning text-dark">弱势防守 (0-50 / 0-1000)</span>：
							两市流动性退潮，鲜有持续主线，以控制回撤、观望为主。
						</li>
					</ul>
				</div>
				<div class="col-12 col-md-5">
					<div class="font-weight-bold mb-1 box-heading">
						🎯 当前图表对比指引：
					</div>
					<ul class="mb-0 pl-3">
						<li>
							<strong>统一坐标轴</strong>：左右两轴采用 1:20 严格对齐，左轴 100 与右轴 2000 处在<strong>同一条水平红线</strong>上。
						</li>
						<li>
							<strong>拖动联动</strong>：拖动下方滑块可自由缩放观察任意历史区间，拖动至最左侧自动回溯更早年份。
						</li>
						<li>
							<strong>点击图例</strong>：可临时隐藏/显示单项指标曲线。
						</li>
					</ul>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import ECharts from 'vue-echarts'
	import axios from 'axios'
	import Moment from 'moment'

	import {
		request_zzquant
	} from "network/request.js"

	export default {
		name: "SentimentMarketCombined",
		props: {
			title: {
				type: String,
				default: "市场结构周期对比"
			},
			subText: {
				type: String,
				default: "年度大周期 · 结构1蓝筹风格 vs 结构2成交额 (共用坐标对比)"
			},
			zoom_start: {
				type: [Number, String],
				default: 50,
			},
			zoom_end: {
				type: Number,
				default: 100,
			}
		},
		components: {
			'v-chart': ECharts
		},
		data() {
			return {
				theme: 'light',
				displayMode: 'combo', // 'combo' (柱+线), 'bar' (双柱), 'line' (双折线)
				axisMode: 'dual',     // 'dual' (双轴), 'single' (单轴归一)
				showSubGrid: false,   // 是否显示量能副图
				chartHeight: 460,
				cachedRawData1: [],
				cachedRawData2: [],
				line: {
					animation: false,
					tooltip: {
						trigger: 'axis',
						axisPointer: {
							type: 'cross'
						}
					},
					legend: {
						top: 5
					},
					grid: [],
					xAxis: [],
					yAxis: [],
					dataZoom: [],
					series: []
				}
			}
		},
		created() {
			this.initTheme();
			this.onThemeChanged = (e) => {
				const t = e && e.detail && e.detail.theme ? e.detail.theme : this.detectTheme();
				if (this.theme !== t) {
					this.theme = t;
					this.renderChart();
				}
			};
			window.addEventListener('site-theme-changed', this.onThemeChanged);
		},
		mounted() {
			this.initTheme();
			this.refresh();
		},
		beforeDestroy() {
			window.removeEventListener('site-theme-changed', this.onThemeChanged);
		},
		methods: {
			detectTheme() {
				try {
					const stored = localStorage.getItem('site.theme');
					if (stored === 'dark' || stored === 'light') return stored;
				} catch (e) {
					// ignore
				}
				return (typeof document !== 'undefined' && document.body.classList.contains('theme-dark')) ? 'dark' : 'light';
			},
			initTheme() {
				this.theme = this.detectTheme();
			},
			setDisplayMode(mode) {
				this.displayMode = mode;
				this.renderChart();
			},
			setAxisMode(mode) {
				this.axisMode = mode;
				this.renderChart();
			},
			toggleSubGrid() {
				this.showSubGrid = !this.showSubGrid;
				this.chartHeight = this.showSubGrid ? 560 : 460;
				this.renderChart();
			},
			calculateMA(dayCount, data) {
				const result = [];
				for (let i = 0, len = data.length; i < len; i++) {
					if (i < dayCount) {
						result.push('-');
						continue;
					}
					let sum = 0;
					for (let j = 0; j < dayCount; j++) {
						sum += Number(data[i - j]) || 0;
					}
					result.push(+(sum / dayCount).toFixed(2));
				}
				return result;
			},
			getScore1Color(val, isDark) {
				if (val >= 200) return isDark ? '#8A0000' : '#820014';
				if (val >= 100) return isDark ? '#ec0000' : '#cf1322';
				if (val >= 50) return isDark ? '#6667de' : '#597ef7';
				return isDark ? '#cac500' : '#b58900';
			},
			getScore2Color(val, isDark) {
				if (val >= 3000) return isDark ? '#8A0000' : '#820014';
				if (val >= 2000) return isDark ? '#ec0000' : '#cf1322';
				if (val >= 1000) return isDark ? '#6667de' : '#597ef7';
				return isDark ? '#cac500' : '#b58900';
			},
			onDataZoom(event, option) {
				if (event.type === 'datazoom') {
					const lastEnd = event.end;
					if (event.start === 0) {
						if (!option.xAxis || !option.xAxis[0] || !option.xAxis[0].data) return;
						const dataStart = option.xAxis[0].data[0];
						const dataStartYear = Moment(dataStart).year();
						if (dataStartYear <= 2014) {
							return;
						}
						const dataStartNew = (dataStartYear - 1) + '-01-01';
						if (this.line.dataZoom && this.line.dataZoom[0]) {
							this.line.dataZoom[0].start = 5;
							this.line.dataZoom[0].end = lastEnd;
						}
						this.refresh(dataStartNew);
					}
				}
			},
			refresh(dataStart) {
				let start = dataStart;
				if (!start) {
					start = (Moment().year() - 2) + '-01-01';
				}
				const url1 = `/v3/api/timing/market/style?date1=${start}`;
				const url2 = `/v3/api/timing/market/style2?date1=${start}`;

				axios.all([
					request_zzquant({ url: url1 }),
					request_zzquant({ url: url2 })
				])
				.then(axios.spread((resp1, resp2) => {
					this.cachedRawData1 = (resp1 && resp1.data && resp1.data.data) || [];
					this.cachedRawData2 = (resp2 && resp2.data && resp2.data.data) || [];
					this.renderChart();
				}))
				.catch(err => {
					console.error("加载市场结构周期数据失败:", err);
				});
			},
			renderChart() {
				if (!this.cachedRawData1.length && !this.cachedRawData2.length) {
					return;
				}

				const isDark = this.theme === 'dark';

				// 1. 结构1 配色 (红/暖色系)
				const s1Color = isDark ? '#ff4d4f' : '#cf1322';
				const s1MA = isDark ? '#fa8c16' : '#d46b08';

				// 2. 结构2 配色 (青蓝/冷色系) - 浅色模式采用高对比度深海电光蓝 #0284c7，深色模式高亮电光青 #00e5ff
				const s2Color = isDark ? '#00e5ff' : '#0284c7';
				const s2MA = isDark ? '#69c0ff' : '#096dd9';

				// 3. 基础界面颜色
				const textColor = isDark ? '#94a3b8' : '#475569';
				const axisLineColor = isDark ? '#475569' : '#cbd5e1';
				const splitLineColor = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)';
				const avgLineColor = isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.35)';

				// 4. 牛市粉色标记背景
				const areaBgColor = isDark ? 'rgba(255, 173, 177, 0.25)' : 'rgba(255, 77, 79, 0.12)';

				// 5. DataZoom 样式
				const dzBorderColor = isDark ? '#2d3748' : '#e2e8f0';
				const dzTextColor = isDark ? '#94a3b8' : '#64748b';
				const dzFillerColor = isDark ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.15)';

				// 6. Tooltip 样式
				const ttBg = isDark ? 'rgba(26, 32, 44, 0.96)' : 'rgba(255, 255, 255, 0.97)';
				const ttBorder = isDark ? '#4a5568' : '#cbd5e1';
				const ttText = isDark ? '#f7fafc' : '#0f172a';
				const ttSub = isDark ? '#94a3b8' : '#64748b';
				const ttDivider = isDark ? '#334155' : '#e2e8f0';
				const ttVal = isDark ? '#ffffff' : '#0f172a';

				// 1. 按日期合并对齐两组数据
				const map1 = new Map(this.cachedRawData1.map(item => [item.date, item]));
				const map2 = new Map(this.cachedRawData2.map(item => [item.date, item]));
				const dateSet = new Set([...map1.keys(), ...map2.keys()]);
				const sortedDates = Array.from(dateSet).sort();

				const categoryData = [];
				const scores1 = [];
				const scores1BarItems = [];
				const scores2Raw = [];
				const scores2Normalized = [];
				const scores2BarItems = [];
				const timings = [];
				const timingAreaData = [];

				// 副图指标
				const upCount = [];
				const upGt5Count = [];
				const turnoverRatio = [];

				let areaItem = [];

				sortedDates.forEach(date => {
					categoryData.push(date);
					const item1 = map1.get(date) || {};
					const item2 = map2.get(date) || {};

					// 结构1数据
					let s1 = item1.score !== undefined ? Number(item1.score) : 0;
					if (s1 > 600) s1 = 600;
					scores1.push(s1);
					scores1BarItems.push({
						value: s1,
						itemStyle: {
							color: this.getScore1Color(s1, isDark)
						}
					});

					// 结构2数据
					let s2 = item2.trade_money_total !== undefined ? Number(item2.trade_money_total) : 0;
					scores2Raw.push(s2);
					const s2Norm = +(s2 / 20).toFixed(2); // 归一化得分 (2000 -> 100)
					scores2Normalized.push(s2Norm);
					scores2BarItems.push({
						value: this.axisMode === 'single' ? s2Norm : s2,
						itemStyle: {
							color: this.getScore2Color(s2, isDark)
						}
					});

					// 副图数据
					upCount.push(item2.up_count || 0);
					upGt5Count.push(item2.up_gt5_count || 0);
					const capCirc = Number(item2.market_cap_circ_total) || 0;
					const tr = capCirc > 0 ? +((s2 / capCirc) * 100).toFixed(2) : 0;
					turnoverRatio.push(tr);

					// 周期区域判定 (timing == -1 标记牛市/强势周期)
					const timing = item1.timing !== undefined ? item1.timing : (item2.timing !== undefined ? item2.timing : 0);
					if (areaItem.length === 0) {
						if (timing === -1) {
							areaItem.push(date);
						}
					} else {
						if (timing === 0) {
							areaItem.push(date);
							timings.push(areaItem);
							areaItem = [];
						} else if (date === sortedDates[sortedDates.length - 1]) {
							areaItem.push(date);
							timings.push(areaItem);
							areaItem = [];
						}
					}
				});

				timings.forEach(item => {
					timingAreaData.push([
						{
							xAxis: item[0],
							itemStyle: { color: areaBgColor }
						},
						{ xAxis: item[1] }
					]);
				});

				// 计算 MA10
				const ma10Score1 = this.calculateMA(10, scores1);
				const ma10Score2Raw = this.calculateMA(10, scores2Raw);
				const ma10Score2Norm = this.calculateMA(10, scores2Normalized);
				const ma10Score2 = this.axisMode === 'single' ? ma10Score2Norm : ma10Score2Raw;
				const score2Data = this.axisMode === 'single' ? scores2Normalized : scores2Raw;

				// 2. 动态计算 Y 轴最大刻度，确保 1:20 绝对水平对齐
				const maxS1 = Math.max(...scores1, 150);
				const maxS2 = Math.max(...scores2Raw, 3000);
				const maxS1Scaled = maxS1 * 20;
				const effectiveMaxS2 = Math.max(maxS1Scaled, maxS2, 8000);
				const rightMax = Math.ceil(effectiveMaxS2 / 2000) * 2000; // 如 8000, 10000
				const leftMax = rightMax / 20; // 对应 400, 500

				// 3. 配置 Grids (增加右侧间距留足右轴刻度宽度)
				const grids = [];
				if (this.showSubGrid) {
					grids.push({
						left: '4.5%',
						right: '5.5%',
						top: '12%',
						height: '52%'
					});
					grids.push({
						left: '4.5%',
						right: '5.5%',
						top: '70%',
						height: '18%'
					});
				} else {
					grids.push({
						left: '4.5%',
						right: '5.5%',
						top: '12%',
						height: '74%'
					});
				}

				// 4. 配置 X 轴
				const xAxes = [
					{
						gridIndex: 0,
						type: 'category',
						data: categoryData,
						scale: true,
						boundaryGap: this.displayMode === 'bar',
						axisLine: { onZero: false, lineStyle: { color: axisLineColor } },
						splitLine: { show: false },
						axisLabel: { fontSize: 10, color: textColor }
					}
				];
				if (this.showSubGrid) {
					xAxes.push({
						gridIndex: 1,
						type: 'category',
						data: categoryData,
						scale: true,
						boundaryGap: false,
						axisLine: { onZero: false, lineStyle: { color: axisLineColor } },
						axisTick: { show: false },
						splitLine: { show: false },
						axisLabel: { show: false }
					});
				}

				// 5. 配置 Y 轴
				const yAxes = [];
				if (this.axisMode === 'dual') {
					// 左轴: 结构1得分 (0 ~ leftMax, 牛市线 100)
					yAxes.push({
						gridIndex: 0,
						name: "结构1得分",
						position: 'left',
						min: 0,
						max: leftMax,
						interval: leftMax / 8,
						axisLabel: { color: s1Color, formatter: '{value}' },
						axisLine: { lineStyle: { color: s1Color } },
						splitLine: {
							show: true,
							lineStyle: { color: splitLineColor, type: 'dashed' }
						},
						nameTextStyle: { color: s1Color, fontSize: 11 }
					});
					// 右轴: 结构2成交额 (0 ~ rightMax, 牛市线 2000)
					yAxes.push({
						gridIndex: 0,
						name: "结构2成交额(亿)",
						position: 'right',
						min: 0,
						max: rightMax,
						interval: rightMax / 8,
						axisLabel: { color: s2Color, formatter: '{value}' },
						axisLine: { lineStyle: { color: s2Color } },
						splitLine: { show: false },
						nameTextStyle: { color: s2Color, fontSize: 11 }
					});
				} else {
					// 单轴归一模式
					yAxes.push({
						gridIndex: 0,
						name: "基准得分 (牛市线=100)",
						position: 'left',
						min: 0,
						max: leftMax,
						interval: leftMax / 8,
						axisLabel: { color: textColor, formatter: '{value}' },
						axisLine: { lineStyle: { color: axisLineColor } },
						splitLine: {
							show: true,
							lineStyle: { color: splitLineColor, type: 'dashed' }
						},
						nameTextStyle: { color: textColor, fontSize: 11 }
					});
				}

				if (this.showSubGrid) {
					// 副图 Y 轴
					yAxes.push({
						gridIndex: 1,
						scale: true,
						position: 'right',
						splitNumber: 2,
						axisLabel: { color: textColor, fontSize: 9 },
						axisLine: { lineStyle: { color: axisLineColor } },
						splitLine: { show: false }
					});
				}

				// 6. 配置 Series
				const series = [];

				// 共享的牛市警戒线与周期背景
				const markLineCommon = {
					symbol: 'none',
					data: [
						{
							type: 'average',
							name: '均值',
							lineStyle: { type: 'solid', color: avgLineColor, width: 1 }
						},
						{
							yAxis: 100,
							lineStyle: { type: 'dashed', color: s1Color, width: 1.5 },
							label: {
								position: 'insideStartTop',
								formatter: this.axisMode === 'dual' ? '牛市线(S1:100 / S2:2000)' : '牛市基准(100)',
								color: s1Color,
								fontSize: 11,
								fontWeight: 'bold',
								padding: [2, 4],
								backgroundColor: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.85)',
								borderRadius: 3
							}
						}
					]
				};

				const markAreaCommon = {
					data: timingAreaData
				};

				// ---- 结构1 Series ----
				if (this.displayMode === 'bar' || this.displayMode === 'combo') {
					series.push({
						name: '结构1-得分',
						type: 'bar',
						yAxisIndex: 0,
						xAxisIndex: 0,
						data: scores1BarItems,
						barMaxWidth: 10,
						markLine: markLineCommon,
						markArea: markAreaCommon
					});
				} else {
					series.push({
						name: '结构1-得分',
						type: 'line',
						yAxisIndex: 0,
						xAxisIndex: 0,
						data: scores1,
						showSymbol: false,
						lineStyle: { width: 2, color: s1Color },
						markLine: markLineCommon,
						markArea: markAreaCommon
					});
				}

				// 结构1 MA10 (虚线，体现次要地位)
				series.push({
					name: '结构1-MA10',
					type: 'line',
					yAxisIndex: 0,
					xAxisIndex: 0,
					data: ma10Score1,
					showSymbol: false,
					lineStyle: {
						width: 1.2,
						type: 'dashed',
						color: s1MA,
						opacity: 0.85
					}
				});

				// ---- 结构2 Series ----
				const s2YAxisIdx = this.axisMode === 'dual' ? 1 : 0;

				if (this.displayMode === 'bar') {
					series.push({
						name: this.axisMode === 'dual' ? '结构2-成交额' : '结构2-得分(换算)',
						type: 'bar',
						yAxisIndex: s2YAxisIdx,
						xAxisIndex: 0,
						data: scores2BarItems,
						barMaxWidth: 10
					});
				} else if (this.displayMode === 'combo') {
					// 组合模式: 结构2 作为折线 + 面积平滑流线
					series.push({
						name: this.axisMode === 'dual' ? '结构2-成交额' : '结构2-得分(换算)',
						type: 'line',
						smooth: true,
						yAxisIndex: s2YAxisIdx,
						xAxisIndex: 0,
						data: score2Data,
						showSymbol: false,
						lineStyle: { width: 2, color: s2Color },
						areaStyle: {
							color: {
								type: 'linear',
								x: 0,
								y: 0,
								x2: 0,
								y2: 1,
								colorStops: [
									{ offset: 0, color: isDark ? 'rgba(0, 229, 255, 0.18)' : 'rgba(2, 132, 199, 0.14)' },
									{ offset: 1, color: isDark ? 'rgba(0, 229, 255, 0.01)' : 'rgba(2, 132, 199, 0.01)' }
								]
							}
						}
					});
				} else {
					// 双折线模式
					series.push({
						name: this.axisMode === 'dual' ? '结构2-成交额' : '结构2-得分(换算)',
						type: 'line',
						smooth: true,
						yAxisIndex: s2YAxisIdx,
						xAxisIndex: 0,
						data: score2Data,
						showSymbol: false,
						lineStyle: { width: 2, color: s2Color }
					});
				}

				// 结构2 MA10 (虚线，体现次要地位)
				series.push({
					name: '结构2-MA10',
					type: 'line',
					smooth: true,
					yAxisIndex: s2YAxisIdx,
					xAxisIndex: 0,
					data: ma10Score2,
					showSymbol: false,
					lineStyle: {
						width: 1.2,
						type: 'dashed',
						color: s2MA,
						opacity: 0.85
					}
				});

				// ---- 副图指标 Series (可选) ----
				if (this.showSubGrid) {
					series.push({
						name: '上涨家数',
						type: 'line',
						xAxisIndex: 1,
						yAxisIndex: yAxes.length - 1,
						data: upCount,
						showSymbol: false,
						lineStyle: { width: 1.2, color: '#f5222d' }
					});
					series.push({
						name: '涨幅>5%家数',
						type: 'line',
						xAxisIndex: 1,
						yAxisIndex: yAxes.length - 1,
						data: upGt5Count,
						showSymbol: false,
						lineStyle: { width: 1.2, color: '#faad14' }
					});
					series.push({
						name: '换手率(%)',
						type: 'line',
						xAxisIndex: 1,
						yAxisIndex: yAxes.length - 1,
						data: turnoverRatio,
						showSymbol: false,
						lineStyle: { width: 1.2, color: '#eb2f96' }
					});
				}

				// 7. DataZoom
				const zoomXIndices = this.showSubGrid ? [0, 1] : [0];
				const dataZoom = [
					{
						show: true,
						type: 'slider',
						xAxisIndex: zoomXIndices,
						bottom: '2%',
						start: this.zoom_start,
						end: this.zoom_end,
						textStyle: { color: dzTextColor },
						borderColor: dzBorderColor,
						fillerColor: dzFillerColor
					}
				];

				// 8. Custom Tooltip Formatter
				const tooltip = {
					trigger: 'axis',
					axisPointer: {
						type: 'cross',
						label: { backgroundColor: isDark ? '#4a5568' : '#64748b' }
					},
					backgroundColor: ttBg,
					borderColor: ttBorder,
					borderWidth: 1,
					extraCssText: isDark ? 'box-shadow: 0 4px 14px rgba(0,0,0,0.4);' : 'box-shadow: 0 4px 14px rgba(0,0,0,0.12);',
					textStyle: { color: ttText, fontSize: 12 },
					formatter: (params) => {
						if (!params || !params.length) return '';
						const date = params[0].axisValue;
						const idx = categoryData.indexOf(date);
						const s1 = idx >= 0 ? scores1[idx] : '-';
						const ma1 = idx >= 0 ? ma10Score1[idx] : '-';
						const s2 = idx >= 0 ? scores2Raw[idx] : '-';
						const ma2 = idx >= 0 ? ma10Score2Raw[idx] : '-';
						const s2Norm = idx >= 0 ? scores2Normalized[idx] : '-';

						let cycleText = '弱势防守区';
						let cycleColor = isDark ? '#cac500' : '#b58900';
						if (s1 > 100 && s2 > 2000) {
							cycleText = '🔥 确定性牛市共振 (蓝筹主线抱团)';
							cycleColor = '#ff4d4f';
						} else if (s1 >= 50 || s2 >= 1000) {
							cycleText = '⚡ 局部轮动震荡区';
							cycleColor = isDark ? '#00e5ff' : '#0284c7';
						}

						const s1Tag = s1 >= 200 ? '极强' : s1 >= 100 ? '强势' : s1 >= 50 ? '震荡' : '弱势';
						const s2Tag = s2 >= 3000 ? '天量' : s2 >= 2000 ? '放量' : s2 >= 1000 ? '常态' : '地量';

						let html = `<div style="font-weight:600;font-size:13px;margin-bottom:6px;border-bottom:1px solid ${ttDivider};padding-bottom:4px;color:${ttVal};">
							${date}
						</div>`;

						html += `<div style="margin-bottom:4px;color:${ttText};">
							<span style="display:inline-block;width:10px;height:10px;background:${s1Color};border-radius:50%;margin-right:6px;"></span>
							<strong>结构1 (蓝筹风格)</strong>: <span style="font-size:13px;font-weight:600;color:${ttVal}">${s1}</span> [${s1Tag}]
							<span style="color:${ttSub};margin-left:8px;font-size:11px;">(MA10: <span style="color:${s1MA}">${ma1}</span>)</span>
						</div>`;

						html += `<div style="margin-bottom:4px;color:${ttText};">
							<span style="display:inline-block;width:10px;height:10px;background:${s2Color};border-radius:50%;margin-right:6px;"></span>
							<strong>结构2 (成交额)</strong>: <span style="font-size:13px;font-weight:600;color:${ttVal}">${s2} 亿</span> [${s2Tag}]
							<span style="color:${ttSub};margin-left:8px;font-size:11px;">(MA10: <span style="color:${s2MA}">${ma2} 亿</span>)</span>
						</div>`;

						html += `<div style="font-size:11px;color:${ttSub};margin-top:4px;border-top:1px dashed ${ttDivider};padding-top:3px;">
							共振状态: <span style="color:${cycleColor};font-weight:600;">${cycleText}</span>
						</div>`;

						if (this.axisMode === 'single') {
							html += `<div style="font-size:11px;color:${ttSub};">
								结构2归一得分: <span style="color:${s2Color};">${s2Norm}</span> (基准线 100)
							</div>`;
						}

						if (this.showSubGrid && idx >= 0) {
							html += `<div style="font-size:11px;color:${ttSub};margin-top:4px;">
								上涨: ${upCount[idx]} | &gt;5%: ${upGt5Count[idx]} | 换手率: ${turnoverRatio[idx]}%
							</div>`;
						}

						return html;
					}
				};

				this.line = {
					animation: false,
					grid: grids,
					xAxis: xAxes,
					yAxis: yAxes,
					series: series,
					dataZoom: dataZoom,
					tooltip: tooltip,
					legend: {
						data: [
							{ name: '结构1-得分', icon: this.displayMode === 'line' ? 'line' : 'roundRect' },
							{ name: '结构1-MA10', icon: 'line' },
							{ name: this.axisMode === 'dual' ? '结构2-成交额' : '结构2-得分(换算)', icon: this.displayMode === 'bar' ? 'roundRect' : 'line' },
							{ name: '结构2-MA10', icon: 'line' },
							...(this.showSubGrid ? [
								{ name: '上涨家数', icon: 'line' },
								{ name: '涨幅>5%家数', icon: 'line' },
								{ name: '换手率(%)', icon: 'line' }
							] : [])
						],
						itemWidth: 20,
						itemHeight: 8,
						textStyle: { color: textColor, fontSize: 11 },
						top: 5
					}
				};
			}
		}
	}
</script>

<style scoped>
	.sentiment-combined-container {
		width: 100%;
	}
	.chart-main-title {
		color: #1e293b;
		font-weight: 600;
		font-size: 1.15rem;
		transition: color 0.25s ease;
	}
	.chart-sub-title {
		color: #64748b;
		transition: color 0.25s ease;
	}
	.chart_combined {
		width: 100%;
		background-color: transparent;
	}
	.toolbar-group .btn {
		font-size: 0.78rem;
		padding: 0.2rem 0.55rem;
	}
	.explanation-box {
		background-color: #f8fafc !important;
		border: 1px solid #e2e8f0 !important;
		color: #475569 !important;
		border-radius: 8px;
		transition: background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease;
	}
	.explanation-box .box-heading {
		color: #0f172a !important;
	}
	.explanation-box strong {
		color: #1e293b;
	}
	.explanation-box li {
		line-height: 1.6;
		margin-bottom: 2px;
	}

	/* 深色皮肤适配 (通过 body.theme-dark) */
	body.theme-dark .chart-main-title {
		color: #f1f5f9;
	}
	body.theme-dark .chart-sub-title {
		color: #94a3b8;
	}
	body.theme-dark .explanation-box {
		background-color: #1a202c !important;
		border: 1px solid #2d3748 !important;
		color: #a0aec0 !important;
	}
	body.theme-dark .explanation-box .box-heading {
		color: #e2e8f0 !important;
	}
	body.theme-dark .explanation-box strong {
		color: #cbd5e1;
	}
</style>
